/**
 * Research Data landing page — /research/data
 * Lists available datasets and links to individual data pages
 */
import React, { useState } from "react";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";

interface Dataset {
  title: string;
  date: string;
  description: string;
  href: string;
  badge: string;
  stats: { value: string; label: string }[];
}

const DATASETS: Dataset[] = [
  {
    title: "B2B SERP Feature Landscape 2026",
    date: "March 2026",
    description: "Which SERP features dominate B2B search — and how many queries now carry zero-click risk? AI Overviews, Featured Snippets, PAA, and more across 200 B2B keywords.",
    href: "/data/b2b-serp-features-2026",
    badge: "Original Data",
    stats: [
      { value: "200", label: "B2B keywords checked" },
      { value: "6", label: "SERP features tracked" },
      { value: "10", label: "B2B categories" },
    ],
  },
  {
    title: "AI Overview Penetration by Buyer Journey Stage 2026",
    date: "March 2026",
    description: "TOFU content is overwhelmingly captured by AI Overviews. BOFU keywords are almost entirely unaffected. Data across 90 B2B keywords by funnel stage.",
    href: "/data/ai-overview-by-funnel-stage",
    badge: "Original Data",
    stats: [
      { value: "90", label: "B2B keywords analyzed" },
      { value: "3", label: "Funnel stages: TOFU / MOFU / BOFU" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
  {
    title: "B2B Organic CTR by SERP Position 2026",
    date: "March 2026",
    description: "CTR curves with and without AI Overviews, positions 1–10. The definitive argument for why ranking #1 is no longer enough in B2B search.",
    href: "/data/b2b-ctr-by-position-2026",
    badge: "Original Data",
    stats: [
      { value: "10", label: "SERP positions benchmarked" },
      { value: "~31%", label: "position 1 CTR drop with AI Overview" },
      { value: "100+", label: "B2B keywords in sample" },
    ],
  },
  {
    title: "Domain Rating Requirements to Rank Page 1 by B2B Category 2026",
    date: "March 2026",
    description: "What DR score does your site actually need? Median, P25, and P75 domain ratings across the top-10 results for 55 B2B keyword SERPs.",
    href: "/data/domain-rating-benchmarks-b2b",
    badge: "Original Data",
    stats: [
      { value: "550", label: "Ahrefs DR data points" },
      { value: "11", label: "B2B industries" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
  {
    title: "B2B Keyword Intent Distribution by Category 2026",
    date: "March 2026",
    description: "Most B2B SEO budgets are spent on informational content — but what % of B2B keywords are actually commercial? Intent breakdown across 40 B2B keywords.",
    href: "/data/b2b-keyword-intent-distribution",
    badge: "Original Data",
    stats: [
      { value: "40", label: "B2B keywords classified" },
      { value: "4", label: "Intent types analyzed" },
      { value: "5", label: "B2B industries" },
    ],
  },
  {
    title: "Ranking Content Length Benchmarks for B2B 2026",
    date: "March 2026",
    description: "What word count do top-ranking B2B pages actually have? Data from 40 B2B keywords shows the answer varies significantly by intent — and the 'longer is better' myth doesn't hold.",
    href: "/data/content-length-benchmarks-b2b",
    badge: "Original Data",
    stats: [
      { value: "40", label: "B2B keywords analyzed" },
      { value: "2", label: "Intent types compared" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
  {
    title: "B2B Search Volume Trends: 2024 vs. 2026",
    date: "March 2026",
    description: "Which B2B keyword categories are growing in search demand — and which are declining as AI answers more queries? YoY comparison across 50 B2B keywords.",
    href: "/data/b2b-keyword-search-volume-trends",
    badge: "Original Data",
    stats: [
      { value: "50", label: "B2B keywords tracked" },
      { value: "2yr", label: "2024 vs. 2026 comparison" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
  {
    title: "Cost Per Click vs. Organic Click Value in B2B 2026",
    date: "March 2026",
    description: "If you had to buy every organic click, what would your SEO be worth? CPC and estimated annual click value across 50 B2B keywords, by industry.",
    href: "/data/cpc-vs-organic-value-b2b",
    badge: "Original Data",
    stats: [
      { value: "50", label: "B2B keywords with CPC data" },
      { value: "10", label: "B2B industries" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
  {
    title: "Question Keywords and AI Overview Exposure in B2B 2026",
    date: "March 2026",
    description: "'What is' and 'How to' queries face very different AI Overview rates than 'Compare' queries. Data across 80 question-format B2B keywords by query type.",
    href: "/data/question-keywords-ai-overview-rate",
    badge: "Original Data",
    stats: [
      { value: "80", label: "Question-format keywords" },
      { value: "4", label: "Query types: What / How / Best / Compare" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
  {
    title: "Backlink Requirements to Rank Top 5 in B2B 2026",
    date: "March 2026",
    description: "How many referring domains does a page actually need? Median and range of RDs for top-5 results across 55 B2B keyword SERPs — by industry.",
    href: "/data/backlink-benchmarks-b2b",
    badge: "Original Data",
    stats: [
      { value: "275", label: "Pages analyzed" },
      { value: "11", label: "B2B industries" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
  {
    title: "SEO Benchmarks by Industry 2026",
    date: "March 2026",
    description: "Keyword difficulty, organic click-through rates, and AI Overview penetration across 11 B2B verticals. Based on analysis of 55 B2B keywords via Ahrefs.",
    href: "/data/seo-benchmarks-by-industry",
    badge: "Original Data",
    stats: [
      { value: "11", label: "B2B verticals" },
      { value: "55", label: "Keywords analyzed" },
      { value: "March 2026", label: "Data collected" },
    ],
  },
];

function DatasetCard({ dataset, isMobile, featured = false }: { dataset: Dataset; isMobile: boolean; featured?: boolean }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={dataset.href}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        textDecoration: "none",
        border: `1px solid ${hov ? "rgba(87,215,226,0.3)" : "rgba(255,255,255,0.07)"}`,
        borderRadius: "16px",
        background: hov ? "rgba(87,215,226,0.03)" : "#111",
        overflow: "hidden",
        transition: "border-color 0.2s, background 0.2s, transform 0.2s",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        height: "100%",
      }}
    >
      {/* Top section */}
      <div style={{ padding: isMobile ? "24px 20px 20px" : featured ? "36px 40px 28px" : "28px 32px 24px", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px", flexWrap: "wrap" }}>
          <span style={{
            display: "inline-block", padding: "3px 10px",
            borderRadius: "4px", fontSize: "10px", fontWeight: 700,
            fontFamily: ff, letterSpacing: "0.08em", textTransform: "uppercase",
            color: cyan, background: "rgba(87,215,226,0.1)",
            border: "1px solid rgba(87,215,226,0.2)",
          }}>
            {dataset.badge}
          </span>
          <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "12px", fontFamily: ff }}>
            {dataset.date}
          </span>
        </div>
        <h2 style={{
          color: "white",
          fontSize: isMobile ? "18px" : featured ? "clamp(22px, 2vw, 26px)" : "17px",
          fontWeight: 800,
          fontFamily: ff,
          lineHeight: 1.25,
          letterSpacing: "-0.01em",
          marginBottom: "10px",
        }}>
          {dataset.title}
        </h2>
        <p style={{
          color: "rgba(255,255,255,0.45)",
          fontSize: featured ? "15px" : "14px",
          fontFamily: ff,
          lineHeight: 1.65,
          marginBottom: "20px",
          maxWidth: featured ? "600px" : "none",
        }}>
          {dataset.description}
        </p>
        <span style={{
          color: hov ? "#57d7e2" : "rgba(87,215,226,0.7)",
          fontSize: "13px", fontWeight: 600, fontFamily: ff,
          display: "inline-flex", alignItems: "center", gap: "5px",
          transition: "color 0.15s",
        }}>
          View dataset
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ transform: hov ? "translateX(2px)" : "translateX(0)", transition: "transform 0.15s" }}>
            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      {/* Stat strip */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : `repeat(${dataset.stats.length}, 1fr)`,
        borderTop: "1px solid rgba(255,255,255,0.06)",
      }}>
        {dataset.stats.map((stat, i) => (
          <div key={i} style={{
            padding: isMobile ? "16px 20px" : featured ? "20px 40px" : "16px 32px",
            borderLeft: (!isMobile && i > 0) ? "1px solid rgba(255,255,255,0.06)" : "none",
            borderTop: (isMobile && i > 0) ? "1px solid rgba(255,255,255,0.06)" : "none",
          }}>
            <div style={{
              color: cyan, fontSize: featured ? "22px" : "18px", fontWeight: 700,
              fontFamily: ff, lineHeight: 1, marginBottom: "4px",
            }}>
              {stat.value}
            </div>
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: "12px", fontFamily: ff, lineHeight: 1.4 }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>
    </a>
  );
}

export default function ResearchDataLanding() {
  const isMobile = useIsMobile();

  useSeoMeta({
    title: "B2B SEO Data & Benchmarks 2026 — Indexed Research",
    description: "Free B2B SEO benchmarks and datasets across 11 industries, including CTR, AI Overview rates, and keyword intent data. See how your site compares.",
    canonical: "/data",
    ogType: "website",
    jsonLd: [{
      "@type": "Dataset",
      "name": "B2B SEO Benchmarks & Research Data 2026",
      "description": "Original B2B SEO data from Indexed covering CTR by SERP position, AI Overview penetration, domain rating benchmarks, keyword intent distribution, and content length norms.",
      "url": "https://www.joinindexed.com/data",
      "creator": { "@type": "Organization", "name": "Indexed", "url": "https://www.joinindexed.com" },
      "datePublished": "2026-03-01",
      "license": "https://creativecommons.org/licenses/by/4.0/"
    }],
  });

  return (
    <SiteLayout>
      {/* Hero */}
      {/* Responsive layout is CSS-driven (media query at the 900px breakpoint),
          NOT JS isMobile branching. The hero is prerendered to static HTML at a
          mobile viewport; if its responsive values came from isMobile inline
          styles they'd bake as mobile and visibly reflow when React hydrates at
          desktop width. Keeping them in this <style> block means the prerendered
          HTML carries the media query and paints the correct layout immediately,
          so nothing shifts on load. */}
      <style>{`
        .rdl-hero-section { padding: 200px 72px 80px; }
        .rdl-hero-h1 { font-size: clamp(40px, 5vw, 54px); }
        @media (max-width: 900px) {
          .rdl-hero-section { padding: 160px 20px 60px; }
          .rdl-hero-h1 { font-size: 34px; }
        }
      `}</style>
      <section className="rdl-hero-section" style={{
        background: "#0a0a0a",
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <h1 className="rdl-hero-h1" style={{
            color: "white",
            fontWeight: 700,
            fontFamily: ff,
            lineHeight: 1.1,
            letterSpacing: "-0.02em",
            marginBottom: "20px",
          }}>
            Stop guessing. <span style={{ color: cyan }}>Start with the data.</span>
          </h1>
          <p style={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "18px",
            fontFamily: ff,
            lineHeight: 1.7,
            maxWidth: "600px",
            marginBottom: "28px",
          }}>
            Most B2B SEO decisions are made on gut feel. These datasets change that. CTR benchmarks, AI Overview exposure rates, domain authority requirements, content length norms — all sourced from real B2B SERPs so you know exactly what you're up against before you invest a single dollar.
          </p>
          <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
            {[
              { value: "11", label: "datasets" },
              { value: "800+", label: "keywords analyzed" },
              { value: "Free", label: "no signup required" },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span style={{ color: cyan, fontSize: "22px", fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{s.value}</span>
                <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: ff }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dataset cards */}
      <section style={{
        background: "#0a0a0a",
        padding: isMobile ? "0 20px 60px" : "0 72px 80px",
      }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          {/* Featured first card — full width */}
          <div style={{ marginBottom: "16px" }}>
            <DatasetCard dataset={DATASETS[0]} isMobile={isMobile} featured />
          </div>
          {/* Remaining cards — 2-column grid on desktop */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "16px",
          }}>
            {DATASETS.slice(1).map(d => (
              <DatasetCard key={d.href} dataset={d} isMobile={isMobile} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: "#0c0c0c",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        padding: isMobile ? "60px 20px" : "80px 72px",
      }}>
        <div style={{ maxWidth: "640px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{
            color: "white",
            fontSize: isMobile ? "26px" : "36px",
            fontWeight: 700,
            fontFamily: ff,
            lineHeight: 1.15,
            marginBottom: "16px",
          }}>
            Want this analysis for your keywords?
          </h2>
          <p style={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "17px",
            fontFamily: ff,
            lineHeight: 1.6,
            marginBottom: "32px",
          }}>
            We run custom AI Overview exposure audits for B2B companies. See exactly which of your keywords are affected — and what it's costing you.
          </p>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0" }}>
            <a
              href={STRATEGIST_URL}
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "16px 32px",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                borderRadius: "88px", border: "1px solid #57d7e2",
                boxShadow: "0px 4px 20px rgba(87,215,226,0.25)",
                textDecoration: "none",
                color: "#0a0c0c", fontSize: "15px", fontWeight: 700, fontFamily: ff,
                transition: "opacity 0.2s, transform 0.2s",
              }}
              onMouseEnter={e => {
                e.currentTarget.style.opacity = "0.88";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.transform = "translateY(0)";
              }}
            >
              Get a custom analysis
            </a>
            <a
              href="/ai-seo"
              style={{ color: "rgba(255,255,255,0.4)", fontSize: "13px", fontFamily: ff, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "5px", marginTop: "16px" }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.color = "#57d7e2"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.4)"}
            >
              Learn about our AI SEO service
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
